/**
 * Left sidebar timeline showing all days and transit segments.
 * Day items are droppable targets — drag an event onto a day to move it there.
 */

import { useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDroppable } from '@dnd-kit/core';

/** Wraps the entire sidebar so gaps between day items are also droppable */
function SidebarDropZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({ id: 'timeline-sidebar' });
  return (
    <div ref={setNodeRef} className="flex flex-col h-full">
      {children}
    </div>
  );
}
import type { TravelItemResponse, TravelTripResponse } from '../api';
import { formatDayDate, TRANSIT_MODE_EMOJI } from './TravelHelpers';
import { useFlightStatus } from '../hooks';

interface TravelTimelineProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  activeItemId: string | null;
  /** Which day item is being hovered during a drag */
  dragOverItemId?: string | null;
  onSelectItem: (itemId: string) => void;
  onUpdateItem: (itemId: string, input: Record<string, unknown>) => Promise<unknown>;
  onAddDay: () => void;
  onAddTransit: () => void;
}

/**
 * Small indicator dot shown next to a transit item in the timeline. Renders
 * only for Flight transits when the `flight_status` feature is on AND the
 * live status is notable (delayed / cancelled / diverted). A landed or
 * on-schedule flight shows nothing, keeping the timeline quiet by default.
 *
 * The hook is a no-op for non-Flight items, so invoking it here cheaply
 * covers every transit row — the component renders null for the common case.
 */
function TransitFlightDot({ item }: { item: TravelItemResponse }) {
  const { snapshot } = useFlightStatus(item);
  const status = snapshot?.status ?? null;
  if (
    !status ||
    status === 'scheduled' ||
    status === 'active' ||
    status === 'landed' ||
    status === 'unknown'
  ) {
    return null;
  }
  const cls = status === 'cancelled' || status === 'diverted' ? 'bg-destructive' : 'bg-amber-500';
  const title =
    status === 'cancelled'
      ? 'Flight cancelled'
      : status === 'diverted'
        ? 'Flight diverted'
        : 'Flight delayed';
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${cls}`} title={title} aria-label={title} />
  );
}

function DayTimelineItem({
  item,
  trip,
  isActive,
  isDragOver,
  onSelect,
  onUpdateItem,
}: {
  item: TravelItemResponse;
  trip: TravelTripResponse;
  isActive: boolean;
  isDragOver: boolean;
  onSelect: () => void;
  onUpdateItem: (itemId: string, input: Record<string, unknown>) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Make this day a drop target for events
  const { setNodeRef, isOver } = useDroppable({
    id: `timeline-${item.itemId}`,
  });

  const highlighted = isDragOver || isOver;

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(item.dayTitle || '');
    setEditing(true);
  };

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed !== (item.dayTitle || '')) {
      onUpdateItem(item.itemId, { dayTitle: trimmed || null });
    }
    setEditing(false);
  };

  const dayLabel = item.dayTitle || `Day ${item.dayNumber}`;

  return (
    <div ref={setNodeRef} className="relative">
      <button
        key={item.itemId}
        onClick={onSelect}
        className={`group w-full text-left px-3 py-2 rounded-md transition-colors ${
          highlighted
            ? 'ring-2 ring-primary bg-primary/10'
            : isActive
              ? 'bg-foreground text-background'
              : 'hover:bg-muted'
        }`}
      >
        <div className="text-xs opacity-70">
          {formatDayDate(trip.startDate, item.dayNumber ?? 1)}
        </div>
        {editing ? (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') setEditing(false);
            }}
            onClick={(e) => e.stopPropagation()}
            placeholder={`Day ${item.dayNumber}`}
            className="h-6 text-sm font-semibold mt-0.5 px-1 py-0"
            autoFocus
          />
        ) : (
          <div className="flex items-center gap-1 mt-0.5">
            <span className="font-semibold text-sm truncate">{dayLabel}</span>
            <Pencil
              className={`h-2.5 w-2.5 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity cursor-pointer ${
                isActive ? 'hover:opacity-100' : 'hover:opacity-100'
              }`}
              onClick={startEditing}
            />
          </div>
        )}
        {item.city && (
          <div className="text-xs opacity-60 mt-0.5">
            {item.city}
            {item.country ? `, ${item.country}` : ''}
          </div>
        )}
      </button>

      {/* "Move to" tooltip — shown while dragging over this day */}
      {highlighted && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 whitespace-nowrap bg-popover border border-border shadow-md rounded-md px-2.5 py-1.5 text-xs font-medium pointer-events-none">
          Move to <span className="font-bold">{dayLabel}</span>
        </div>
      )}
    </div>
  );
}

export function TravelTimeline({
  trip,
  items,
  activeItemId,
  dragOverItemId,
  onSelectItem,
  onUpdateItem,
  onAddDay,
  onAddTransit,
}: TravelTimelineProps) {
  return (
    <SidebarDropZone>
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-1 p-2">
          {items.map((item) => {
            const isActive = item.itemId === activeItemId;

            if (item.itemType === 'day') {
              return (
                <DayTimelineItem
                  key={item.itemId}
                  item={item}
                  trip={trip}
                  isActive={isActive}
                  isDragOver={dragOverItemId === item.itemId}
                  onSelect={() => onSelectItem(item.itemId)}
                  onUpdateItem={onUpdateItem}
                />
              );
            }

            // Transit segment
            return (
              <button
                key={item.itemId}
                onClick={() => onSelectItem(item.itemId)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                  isActive ? 'bg-foreground text-background' : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2 text-xs">
                  <span className="border-l-2 border-dashed border-muted-foreground h-4" />
                  <span className="flex-1">
                    {item.transitMode && TRANSIT_MODE_EMOJI[item.transitMode]}{' '}
                    {item.transitFrom || '?'} → {item.transitTo || '?'}
                  </span>
                  <TransitFlightDot item={item} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-border p-2 space-y-1">
        <Button variant="outline" size="sm" className="w-full" onClick={onAddDay}>
          <Plus className="h-3 w-3 mr-1" /> Add Day
        </Button>
        <Button variant="outline" size="sm" className="w-full" onClick={onAddTransit}>
          <Plus className="h-3 w-3 mr-1" /> Add Transit
        </Button>
      </div>
    </SidebarDropZone>
  );
}
